import { Select } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import React, { FunctionComponent, useState } from 'react';
import { useField } from 'formik';
import { FormField } from '..';
import { DropdownOption, FormFieldPropsBase } from './fieldset.model';

interface SelectFormFieldProps extends Omit<FormFieldPropsBase, 'placeholderTranslationKey'> {
  optionList: Array<DropdownOption>;
  defaultValue?: string;
  /**
   * - By default only first item of the list (placeholder) is rendered or current selection.
   * - Set to true to render all items even if list was not interacted with.
   */
  eagerListRender?: boolean;
  placeholder?: string;
}

const getDefaultPlaceholderText = () => t('formControls:select.noSelection');

export const SelectFormField: FunctionComponent<SelectFormFieldProps> = ({
  optionList,
  labelTranslationKey = '',
  isDisabled,
  fieldName,
  testId,
  optional,
  defaultValue,
  eagerListRender,
  placeholder,
}): React.JSX.Element => {
  const [showEntireList, setShowEntireList] = useState(() => eagerListRender);
  const [_fieldProps] = useField(fieldName);

  const hasPlaceholderOption = optionList[0]?.value === '';

  const _placeholder = placeholder ?? (hasPlaceholderOption ? optionList[0] : getDefaultPlaceholderText());

  const _optionList = hasPlaceholderOption
    ? optionList
    : [{ label: _placeholder, value: '' } as DropdownOption, ...optionList];

  const getFirstItemFromOptionList = (): DropdownOption => {
    if (_optionList && _optionList.length > 0) {
      return _optionList[0];
    }

    return { value: '', label: t('formControls:select.loading') };
  };

  const listToRender = showEntireList
    ? _optionList
    : // Show either current selection or first element that should be placeholder
      [_optionList.find(({ value: _value }) => _value === _fieldProps.value) ?? getFirstItemFromOptionList()];

  return (
    <FormField
      type="select"
      name={fieldName}
      testId={testId ?? fieldName}
      labelText={`${t(labelTranslationKey)}${optional ? t('formControls:optionalLabelAffix') : ''}`}
      showEntireList={showEntireList}
      isDisabled={isDisabled}
      validateOnChange
      optionList={_optionList}
      render={(fieldProps) => (
        <Select
          {...fieldProps}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          testId={testId}
          defaultValue={
            defaultValue ?? (_fieldProps.value === undefined ? getFirstItemFromOptionList().value : defaultValue)
          }
          onMouseDownCapture={() => {
            if (eagerListRender) return;
            // eslint-disable-next-line no-console
            console.warn('>> onMouseDownCapture triggered. State will update and full list of items rendered.');
            setShowEntireList(true);
          }}
          onBlurCapture={() => {
            if (eagerListRender) return;
            setShowEntireList(false);
          }}
        >
          {listToRender.map(({ label, value }) => {
            return (
              <Select.Item
                key={`${label}`}
                value={`${value}`}
                disabled={['', undefined, null].includes(value) && !optional /* disable placeholder item */}
              >
                {label}
              </Select.Item>
            );
          })}
        </Select>
      )}
    />
  );
};
